import PropTypes from 'prop-types'
import React, { useContext, useMemo } from 'react'

import FirstBlockWrapper from 'components/_special/FirstBlockWrapper'
import ButtonScrollDown from 'components/ButtonScrollDown'
import { IT, JP, RU } from 'constants/countries'
import { GOODS_TYPES } from 'constants/sales'
import AddAndGoToCart from 'containers/AddAndGoToCart'
import { LocalizationContext } from 'contexts/Localization'
import { MenuMainContext } from 'contexts/MenuMain'
import { Button } from 'shared/Button'
import { Text } from 'shared/Typography'
import { ResizeContext } from 'contexts/Resize'
import { useGoods } from 'modules/Home/components/Carousel/hooks'
import createNumberFormatter from 'utils/createPriceFormatter'

import BHowFirstEuroBox from './components/BHowFirstEuroBox'
import BHowFirstRussianBox from './components/BHowFirstRussianBox'
import { Content, Prices, DiscountBadge, OldPrice, Price } from './styles'

/**
 * BHowFirst
 *
 * @param {object} props - properties of component
 * @returns {node} BHowFirst
 */
const BHowFirst = ({ data, secondBlockRef }) => {
  const { locale, paths, dict } = useContext(LocalizationContext)
  const { isSalesBanner } = React.useContext(MenuMainContext)
  const { mobile } = useContext(ResizeContext)
  const { goods, currency } = useGoods(locale)

  const formatter = useMemo(() => {
    if (!currency) return
    return createNumberFormatter({ locale, currency })
  }, [locale, currency])

  const boxCompiled = useMemo(
    () =>
      locale === RU ? (
        <BHowFirstRussianBox {...{ path: `${paths.cdn_global}/boxes` }} />
      ) : (
        <BHowFirstEuroBox {...{ path: `${paths.cdn_global}/boxes` }} />
      ),
    [locale, paths],
  )
  const price = goods?.microbiome?.price ? formatter.format(goods.microbiome.price) : ''
  const currentPrice = goods?.microbiome?.currentPrice ? formatter.format(goods.microbiome.currentPrice) : ''
  const discount = goods?.microbiome?.discount

  return (
    <FirstBlockWrapper
      isExtraTopIndent={isSalesBanner}
      className='bg--picton-blue text--white pb-2 pb-md-4'
    >
      <div className='row' />

      <div className='row'>
        <div className='col-12 col-md-6'>
          <Content className='first-content-top-indent'>
            <Text
              weight={'medium'}
              size={['50/60', '50/60', '30/40']}
              dangerouslySetInnerHTML={{ __html: data.title }}
            />
            <Text
              size={['18/28', '18/28', '18/28']}
              dangerouslySetInnerHTML={{ __html: data.content }}
            />
          </Content>

          <Prices>
            {price !== currentPrice && (
              <OldPrice>
                <Text
                  className='h-color-W50'
                  as='h5'
                  weight='medium'
                  size={mobile ? '18/24' : '22/30'}
                  decoration='line-through'
                >
                  {price}
                </Text>

                {discount !== 0 && (
                  <DiscountBadge
                    size={['14/20', '14/20', '12/14']}
                    weight='medium'
                  >
                    -{discount}%
                  </DiscountBadge>
                )}
              </OldPrice>
            )}

            <Price>
              {![JP, IT].includes(locale) && (
                <Text
                  size={mobile ? '25/30' : '50/60'}
                  weight='medium'
                >
                  {currentPrice}
                </Text>
              )}
            </Price>
          </Prices>

          <div className='d-flex align-items-center'>
            <AddAndGoToCart
              goods={[
                {
                  type: GOODS_TYPES.biome,
                  isSubscription: false,
                },
              ]}
              buttonProps={{
                color: 'white',
                size: mobile ? 'mobile' : 'desktop'
              }}
              ButtonComponent={Button}
              buttonTitleKey={'order_test'}
              forceMultipleAdd={true}
            >
              {dict.buttons.add_to_cart}
            </AddAndGoToCart>
          </div>
        </div>
        <div className='col-12 col-md-6'>{boxCompiled}</div>
      </div>

      <div className='row'>
        <div className='col-12 pt-4'>
          <ButtonScrollDown
            title={data.go_to_site}
            refToScroll={secondBlockRef}
            isLightColor={true}
          />
        </div>
      </div>
    </FirstBlockWrapper>
  )
}

BHowFirst.propTypes = {
  data: PropTypes.object.isRequired,
  secondBlockRef: PropTypes.object.isRequired,
}

export default BHowFirst
